import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/lib/config";
import { useAppModal } from "@/context/AppModalContext";
import { SITE } from "@/constants/testIds";
import { trackEvent } from "@/lib/analytics";

export default function Header() {
  const { openModal, open: modalOpen } = useAppModal();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close mobile menu whenever the "Get the app" modal opens.
  useEffect(() => {
    if (modalOpen) setMobileOpen(false);
  }, [modalOpen]);

  const handleGetApp = (placement) => {
    trackEvent("get_app_click", { placement });
    openModal(placement);
    setMobileOpen(false);
  };

  return (
    <header
      className="sticky top-0 z-50 w-full bg-white border-b"
      style={{ borderColor: "var(--yn-border)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-3"
            data-testid={SITE.headerLogo}
            aria-label="YeboNadi, home"
          >
            <img
              src={BRAND.logoUrl}
              alt=""
              className="h-11 w-11 sm:h-12 sm:w-12 rounded-lg object-cover ring-1 ring-[color:var(--yn-border)]"
              loading="eager"
              decoding="async"
            />
            <span
              className="font-heading text-lg sm:text-xl font-bold"
              style={{ color: "var(--yn-ink)" }}
            >
              YeboNadi
            </span>
          </Link>

          <nav
            className="hidden lg:flex items-center gap-1"
            data-testid={SITE.headerNav}
            aria-label="Primary"
          >
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                data-testid={`${SITE.headerNavLink}-${link.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? "text-[color:var(--yn-primary)]"
                      : "text-[color:var(--yn-muted)] hover:text-[color:var(--yn-ink)]"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handleGetApp("header")}
              className="hidden sm:inline-flex items-center justify-center text-sm font-semibold text-white bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)] transition-colors px-5 py-2.5 rounded-md"
              data-testid={SITE.headerGetAppBtn}
            >
              Get the app
            </button>
            <button
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              className="lg:hidden p-2 rounded-md text-[color:var(--yn-ink)] hover:bg-[color:var(--yn-subtle)]"
              aria-label="Toggle menu"
              aria-expanded={mobileOpen}
              data-testid={SITE.headerMobileToggle}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div
            className="lg:hidden pb-4"
            data-testid={SITE.headerMobileMenu}
          >
            <nav className="flex flex-col gap-1" aria-label="Mobile">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`${SITE.headerNavLink}-mobile-${link.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `px-3 py-3 text-base font-medium rounded-md ${
                      isActive
                        ? "text-[color:var(--yn-primary)] bg-[color:var(--yn-subtle)]"
                        : "text-[color:var(--yn-ink)] hover:bg-[color:var(--yn-subtle)]"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={() => handleGetApp("header-mobile")}
                className="mt-2 inline-flex items-center justify-center text-base font-semibold text-white bg-[color:var(--yn-primary)] hover:bg-[color:var(--yn-primary-hover)] px-5 py-3 rounded-md"
              >
                Get the app
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
